'use client'

import { useState } from 'react'
import { X, Search, Info } from 'lucide-react'
import { TokenLogo } from './TokenLogo'
import type { Token } from '@/types/token'

interface TokenSelectorProps {
  tokens: Token[]  // Now receives tokens as prop
  onTokenSelect: (token: Token) => void
  onClose: () => void
  selectedToken?: Token | null
  isConnected?: boolean  // Optional, for UI messaging
}

const popularTokens: Token[] = [
  { symbol: 'S', name: 'Sonic', address: 'S', decimals: 18 },
  { symbol: 'wS', name: 'Wrapped Sonic', address: '0x039e2fB66102314Ce7b64Ce5Ce3E5183bc94aD38', decimals: 18 },
  { symbol: 'USDC', name: 'USD Coin', address: '0x29219dd400f2Bf60E5a23d13Be72B486D4038894', decimals: 6 },
  { symbol: 'WETH', name: 'Wrapped Ether', address: '0x50c42dEAcD8Fc9773493ED674b675bE577f2634b', decimals: 18 },
]

export function TokenSelector({
  tokens,
  onTokenSelect,
  onClose,
  selectedToken,
  isConnected = false
}: TokenSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')

  // Filter tokens based on search query
  const filteredTokens = tokens.filter(token =>
    token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
    token.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    token.address.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleTokenSelect = (token: Token) => {
    onTokenSelect(token)
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div>
            <h2 className="text-white text-lg font-semibold">Select a token</h2>
            <p className="text-slate-400 text-sm mt-1">
              {isConnected
                ? "Select a token from your wallet or search by symbol/address."
                : "Select a token from our default list or search for a token by symbol or address."
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by token or address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-700 text-white placeholder:text-slate-400 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Popular Tokens */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {popularTokens.map((token) => (
              <button
                key={token.address}
                onClick={() => handleTokenSelect(token)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 transition-colors cursor-pointer ${selectedToken?.address === token.address
                    ? 'bg-blue-600 hover:bg-blue-500'
                    : 'bg-slate-700 hover:bg-slate-600'
                  }`}
              >
                <TokenLogo
                  tokenAddress={token.address}
                  tokenSymbol={token.symbol}
                  alt={token.symbol}
                  width={20}
                  height={20}
                />
                <span className="text-white text-sm font-medium">{token.symbol}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Token List */}
        <div className="px-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-400 text-sm font-medium">
              {isConnected ? 'Your Tokens' : 'All Tokens'}
            </h3>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredTokens.length === 0 && searchQuery && (
              <div className="text-slate-400 text-sm p-3 text-center">
                No tokens found for "{searchQuery}"
              </div>
            )}

            {filteredTokens.map((token) => (
              <button
                key={token.address}
                onClick={() => handleTokenSelect(token)}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors group cursor-pointer ${selectedToken?.address === token.address
                    ? 'bg-blue-600/20 border border-blue-500'
                    : 'hover:bg-slate-700'
                  }`}
              >
                <div className="flex items-center gap-3">
                  <TokenLogo
                    tokenAddress={token.address}
                    tokenSymbol={token.symbol}
                    alt={token.symbol}
                    width={40}
                    height={40}
                  />
                  <div className="text-left">
                    <div className="text-white font-medium">{token.symbol}</div>
                    <div className="text-slate-400 text-sm">{token.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {token.balance && token.usdValue && (
                    <div className="text-right">
                      <div className="text-white font-medium">{token.balance}</div>
                      <div className="text-slate-400 text-sm">{token.usdValue}</div>
                    </div>
                  )}
                  <Info className="w-4 h-4 text-slate-500 group-hover:text-slate-400 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="h-4"></div>
      </div>
    </div>
  )
}